import { CommessaLink } from "../components/EntityLink";
import InsightButton from "../components/InsightButton";
import PrimoAccessoSezione from "../components/PrimoAccessoSezione";
import React, { useEffect, useState, useMemo, useRef } from "react";
import { collection, query, where, onSnapshot, updateDoc, addDoc, doc, serverTimestamp, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useControlloDoppioni } from "../components/ControlloDoppioni";
import { messaggioErrore } from "../utils/erroriUtente";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { useCanSee } from "../hooks/useCanSee";
import { logModifica } from "../utils/logModifica";
import { Package, CheckCircle, ChevronDown, ChevronUp, X, Plus, AlertTriangle, Lock } from "lucide-react";
import { chiaveBozza, useBozzaModulo } from "../utils/bozzeLocali";
import AvvisoBozza from "../components/AvvisoBozza";
import { parseValutaItaliana, formatValutaItaliana } from "../utils/clienteHelpers";
import { esportaXLSX } from "../utils/esportaXLSX";
import { dominioSito } from "../utils/fornitore";
import { numIt } from "../utils/economiaCommessa";

// Stessa normalizzazione già usata in Rientro Materiali — confronto per descrizione
// insensibile a maiuscole/minuscole e spazi superflui.
const norm = s => (s||"").trim().toLowerCase();
function numero(v) { const n = parseFloat((v??"").toString().replace(",",".")); return isNaN(n) ? 0 : n; }
function escapeHtml(s) { return String(s??"").replace(/[&<>"']/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[c])); }

const STATO_CONFIG = {
  in_attesa:     { label:"In attesa",      colore:"#d97706", bg:"#fff7ed", icon:"⏳" },
  in_lavorazione:{ label:"In lavorazione", colore:"#2563eb", bg:"#eff6ff", icon:"🔄" },
  parziale:      { label:"In evasione",    colore:"#7c3aed", bg:"#ede9fe", icon:"⚠️" },
  chiusa:        { label:"Chiusa",         colore:"#059669", bg:"#d1fae5", icon:"✅" },
  annullata:     { label:"Annullata",      colore:"#9ca3af", bg:"#f3f4f6", icon:"❌" },
};


const UM_OPTIONS = ["pz","mt","ml","kg","lt","rotoli","scatole","sacchi","m²","altro"];

// Definito fuori per evitare perdita focus
function RigaInput({ riga, index, onChange, onRemove, isOnly }) {
  return (
    <div style={{display:"grid",gridTemplateColumns:"2fr 120px 80px 90px 1fr auto",gap:8,alignItems:"center",marginBottom:8}}>
      <input className="input" placeholder="Descrizione materiale *" value={riga.descrizione}
        onChange={e=>onChange(index,"descrizione",e.target.value)}/>
      <input className="input" placeholder="Codice (opz.)" value={riga.codice}
        onChange={e=>onChange(index,"codice",e.target.value)}/>
      <input className="input" type="text" inputMode="decimal" placeholder="Qta" value={riga.quantita}
        onChange={e=>onChange(index,"quantita",e.target.value)}/>
      <select className="input" value={riga.unitaMisura} onChange={e=>onChange(index,"unitaMisura",e.target.value)}>
        {UM_OPTIONS.map(u=><option key={u} value={u}>{u}</option>)}
      </select>
      <input className="input" placeholder="Note (opz.)" value={riga.note}
        onChange={e=>onChange(index,"note",e.target.value)}/>
      <button type="button" onClick={()=>onRemove(index)} disabled={isOnly}
        style={{background:isOnly?"#f3f4f6":"#fee2e2",border:"none",borderRadius:6,width:32,height:32,
          cursor:isOnly?"not-allowed":"pointer",color:isOnly?"#d1d5db":"#dc2626",
          display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        <X size={13}/>
      </button>
    </div>
  );
}

export function NuovaRichiestaMagazzinoModal({ orgId, currentUser, onClose, richiestaEsistente }) {
  const { verifica, finestra: finestraDoppioni } = useControlloDoppioni();
  const { addToast } = useToast();
  const [commesse, setCommesse] = useState([]);
  const [search, setSearch] = useState("");
  const [commessaSelezionata, setCommessaSelezionata] = useState(richiestaEsistente ? { id:richiestaEsistente.projectId, name:richiestaEsistente.projectName, codiceCommessa:richiestaEsistente.codiceCommessa } : null);
  const [righe, setRighe] = useState(richiestaEsistente?.righe?.length ? richiestaEsistente.righe : [{ descrizione:"", codice:"", quantita:"", unitaMisura:"pz", note:"" }]);
  const [urgenza, setUrgenza] = useState(richiestaEsistente?.urgenza || "normale");
  const [motivoUrgenza, setMotivoUrgenza] = useState(richiestaEsistente?.motivoUrgenza || "");
  const [noteGenerali, setNoteGenerali] = useState(richiestaEsistente?.noteGenerali || "");
  const [saving, setSaving] = useState(false);
  // Bozza automatica sul dispositivo (utils/bozzeLocali.js)
  const bozza = useBozzaModulo({
    chiave: chiaveBozza(currentUser?.uid, "richiestaMagazzino", orgId, richiestaEsistente?.id),
    valore: { commessa: commessaSelezionata, righe, urgenza, motivoUrgenza, noteGenerali },
    info: { titolo: `${richiestaEsistente ? "Modifica richiesta" : "Nuova richiesta"} di materiale al magazzino${commessaSelezionata?.name ? ` — ${commessaSelezionata.name}` : ""}`, vai: { page: "ordini-materiali" } },
    applica: d => {
      if (d.commessa !== undefined) setCommessaSelezionata(d.commessa);
      if (Array.isArray(d.righe)) setRighe(d.righe);
      if (d.urgenza) setUrgenza(d.urgenza);
      if (d.motivoUrgenza !== undefined) setMotivoUrgenza(d.motivoUrgenza);
      if (d.noteGenerali !== undefined) setNoteGenerali(d.noteGenerali);
    },
  });

  useEffect(()=>{
    if(!orgId) return;
    const q=query(collection(db,"projects"),where("organizationId","==",orgId),where("statoCommessa","==","attiva"));
    onSnapshot(q,snap=>setCommesse(snap.docs.map(d=>({id:d.id,...d.data()})).filter(p=>!p.archived)));
  },[orgId]);

  const commesseFiltrate = commesse.filter(c=>
    !search || c.name?.toLowerCase().includes(search.toLowerCase()) || (c.codiceCommessa||"").toLowerCase().includes(search.toLowerCase())
  ).sort((a,b)=>a.name?.localeCompare(b.name||"","it"));

  const aggiungiRiga = () => setRighe(r=>[...r,{descrizione:"",codice:"",quantita:"",unitaMisura:"pz",note:""}]);
  const rimuoviRiga = i => setRighe(r=>r.filter((_,idx)=>idx!==i));
  const updateRiga = (i,field,val) => setRighe(r=>r.map((riga,idx)=>idx===i?{...riga,[field]:val}:riga));

  const salva = async (invia) => {
    if (!commessaSelezionata) { addToast("Seleziona una commessa","error"); return; }
    const righeValide = righe.filter(r=>r.descrizione.trim());
    if (righeValide.length===0) { addToast("Inserisci almeno un materiale","error"); return; }
    if (invia && urgenza==="urgente" && !motivoUrgenza.trim()) { addToast("Devi specificare il motivo dell'urgenza per procedere","error"); return; }
    setSaving(true);
    // All'invio: lo stesso materiale è già stato chiesto o ordinato per questa commessa? (W408)
    let campiDoppioni = {};
    if (invia) {
      const esito = await verifica({ orgId, projectId: commessaSelezionata.id, righe: righeValide,
        escludiDoc: richiestaEsistente?.id ? `mag:${richiestaEsistente.id}` : null, nonDoppioni: richiestaEsistente?.nonDoppioni || [] });
      if (!esito.procedi) { setSaving(false); return; }
      campiDoppioni = esito.campi;
    }
    try {
      const dati = {
        ...campiDoppioni,
        projectId: commessaSelezionata.id,
        projectName: commessaSelezionata.name,
        codiceCommessa: commessaSelezionata.codiceCommessa||"",
        organizationId: orgId,
        richiestaFrom: currentUser.uid,
        richiestaFromName: currentUser.displayName||"Utente",
        righe: righeValide,
        urgenza, motivoUrgenza: urgenza==="urgente" ? motivoUrgenza : "", noteGenerali,
        statoRichiesta: invia ? "Inviata" : "Da inviare",
        updatedAt: serverTimestamp(),
      };
      if (invia) dati.stato = "in_attesa";
      if (richiestaEsistente) {
        await updateDoc(doc(db,"richiesteMateriali",richiestaEsistente.id), dati);
      } else {
        await addDoc(collection(db,"richiesteMateriali"),{ ...dati, ...(invia?{stato:"in_attesa"}:{}), createdAt: serverTimestamp() });
      }
      addToast(invia?"Richiesta inviata al magazzino ✅":"Bozza salvata 💾","success");
      onClose();
    } catch(err){ addToast(messaggioErrore(err, "salvare la richiesta"),"error"); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      {finestraDoppioni}
      <div className="modal" style={{maxWidth:700,maxHeight:"92vh",overflowY:"auto"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
          <h3 style={{fontSize:15,fontWeight:700}}>📦 {richiestaEsistente?"Modifica richiesta":"Nuova richiesta di materiale al magazzino"}</h3>
          <button className="btn-icon" onClick={onClose}><X size={16}/></button>
        </div>
        {bozza.trovata && <AvvisoBozza testo={richiestaEsistente ? "Hai modifiche non salvate a questa richiesta" : "Hai una richiesta di materiale iniziata e non salvata"}
          salvataIl={bozza.trovata.salvataIl} onRiprendi={bozza.riprendi} onElimina={bozza.scarta} />}
        <form onSubmit={e=>e.preventDefault()}>
          {/* Selezione commessa con ricerca */}
          <div className="form-group" style={{marginBottom:16}}>
            <label className="form-label">Commessa / Cantiere *</label>
            {commessaSelezionata ? (
              <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",
                background:"#ede9fe",border:"2px solid #7c3aed",borderRadius:8}}>
                <span style={{flex:1,fontSize:13,fontWeight:700,color:"#7c3aed"}}>✓ {commessaSelezionata.name}</span>
                <button type="button" onClick={()=>{setCommessaSelezionata(null);setSearch("");}}
                  style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af",padding:0}}>
                  <X size={14}/>
                </button>
              </div>
            ) : (
              <div>
                <input className="input" value={search} onChange={e=>setSearch(e.target.value)}
                  placeholder="🔍 Cerca per numero o nome commessa..."
                  style={{marginBottom:8}}/>
                {search && (
                  <div style={{border:"1px solid #e5e7eb",borderRadius:8,maxHeight:200,overflowY:"auto",background:"#fff"}}>
                    {commesseFiltrate.length===0 ? (
                      <div style={{padding:"12px 14px",color:"#9ca3af",fontSize:13}}>Nessuna commessa trovata</div>
                    ) : commesseFiltrate.map(c=>(
                      <div key={c.id} onClick={()=>{setCommessaSelezionata(c);setSearch("");}}
                        style={{padding:"10px 14px",cursor:"pointer",fontSize:13,borderBottom:"1px solid #f3f4f6",fontWeight:500}}
                        onMouseOver={e=>e.currentTarget.style.background="#f9f5ff"}
                        onMouseOut={e=>e.currentTarget.style.background="transparent"}>
                        {c.codiceCommessa && <span style={{color:"#7c3aed",fontFamily:"monospace",marginRight:6}}>[{c.codiceCommessa}]</span>}
                        {c.name}
                      </div>
                    ))}
                  </div>
                )}
                {!search && (
                  <div style={{fontSize:11,color:"#9ca3af"}}>Inizia a scrivere il numero o il nome della commessa per trovarla</div>
                )}
              </div>
            )}
          </div>

          {/* Righe materiali */}
          <div className="form-group">
            <label className="form-label">Materiali richiesti</label>
            <div style={{display:"grid",gridTemplateColumns:"2fr 120px 80px 90px 1fr auto",gap:8,marginBottom:4}}>
              {["Descrizione *","Codice","Qta","U.M.","Note",""].map((h,i)=>(
                <div key={i} style={{fontSize:11,fontWeight:600,color:"#9ca3af"}}>{h}</div>
              ))}
            </div>
            {righe.map((riga,i)=>(
              <RigaInput key={i} riga={riga} index={i} onChange={updateRiga} onRemove={rimuoviRiga} isOnly={righe.length===1}/>
            ))}
            <button type="button" onClick={aggiungiRiga}
              style={{display:"flex",alignItems:"center",gap:6,background:"none",
                border:"1px dashed #d1d5db",borderRadius:8,padding:"6px 14px",
                cursor:"pointer",color:"#7c3aed",fontSize:12,fontWeight:600,
                fontFamily:"'Inter',sans-serif",marginBottom:12}}>
              <Plus size={13}/> Aggiungi riga
            </button>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 2fr",gap:12,marginBottom:16}}>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Urgenza</label>
              <div style={{display:"flex",gap:8}}>
                {[{val:"normale",label:"Normale",col:"#059669"},{val:"urgente",label:"🚨 Urgente",col:"#dc2626"}].map(u=>(
                  <button key={u.val} type="button" onClick={()=>setUrgenza(u.val)}
                    style={{flex:1,padding:"7px 0",border:`2px solid ${urgenza===u.val?u.col:"#e5e7eb"}`,
                      borderRadius:8,background:urgenza===u.val?u.col+"15":"#fff",
                      color:urgenza===u.val?u.col:"#6b7280",fontWeight:urgenza===u.val?700:400,
                      cursor:"pointer",fontFamily:"'Inter',sans-serif",fontSize:13}}>
                    {u.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label style={{fontSize:11,fontWeight:600,color:"#6b7280",display:"block",marginBottom:4}}>Note per il magazzino</label>
              <input className="input" value={noteGenerali} onChange={e=>setNoteGenerali(e.target.value)}
                placeholder="Es. Serve entro venerdì mattina..."/>
            </div>
          </div>
          {urgenza==="urgente" && (
            <div style={{marginBottom:16}}>
              <label style={{fontSize:11,fontWeight:700,color:"#dc2626",display:"block",marginBottom:4}}>
                🚨 Motivo dell'urgenza * <span style={{fontWeight:400,color:"#9ca3af"}}>(obbligatorio per le richieste urgenti)</span>
              </label>
              <textarea className="input" value={motivoUrgenza} onChange={e=>setMotivoUrgenza(e.target.value)}
                placeholder="Spiega perché questa richiesta è urgente — es. fermo macchina, penale contrattuale, cantiere bloccato..."
                style={{minHeight:70,resize:"vertical",borderColor:motivoUrgenza.trim()?"#e5e7eb":"#fca5a5",background:motivoUrgenza.trim()?"#fff":"#fef2f2"}}/>
              {!motivoUrgenza.trim() && (
                <div style={{fontSize:11,color:"#dc2626",marginTop:4,fontWeight:600}}>⚠️ Non è possibile inviare una richiesta urgente senza specificare il motivo</div>
              )}
            </div>
          )}

          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annulla</button>
            <button type="button" className="btn btn-secondary" disabled={saving||!commessaSelezionata} onClick={()=>bozza.salvaCon(()=>salva(false))}>
              💾 Salva bozza
            </button>
            <button type="button" className="btn btn-primary" disabled={saving||!commessaSelezionata} onClick={()=>bozza.salvaCon(()=>salva(true))}>
              {saving?"Invio...":"📤 Invia al magazzino"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CostoModal({ richiesta, onClose }) {
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const [costiRighe, setCostiRighe] = useState({});
  const [saving, setSaving] = useState(false);
  const [listino, setListino] = useState([]);

  useEffect(() => {
    if (!richiesta.organizationId) return;
    return onSnapshot(query(collection(db,"listinoMateriali"), where("organizationId","==",richiesta.organizationId)),
      snap => setListino(snap.docs.map(d=>d.data())));
  }, [richiesta.organizationId]);

  // Suggerimento automatico dal Listino Prezzi — solo se descrizione E unità di misura
  // combaciano: meglio lasciare il campo vuoto che dare un numero sbagliato con falsa
  // sicurezza per un'unità diversa. Resta sempre modificabile: è un punto di partenza.
  const suggerisciCosto = (riga, quantitaDaCostare) => {
    const voce = listino.find(v => norm(v.descrizione)===norm(riga.descrizione) && norm(v.unitaMisura)===norm(riga.unitaMisura));
    if (!voce || !(numIt(voce.prezzoUnitario)>0)) return "";
    const totale = quantitaDaCostare * numIt(voce.prezzoUnitario);
    return totale>0 ? formatValutaItaliana(totale) : "";
  };

  const submit = async () => {
    const righeConCosto = Object.entries(costiRighe).filter(([,v])=>parseValutaItaliana(v)>0);
    if (righeConCosto.length===0) { addToast("Inserisci almeno un costo","error"); return; }
    setSaving(true);
    try {
      for (const [idx, valore] of righeConCosto) {
        const riga = richiesta.righe[idx];
        const importo = parseValutaItaliana(valore);
        await addDoc(collection(db,"movimentiEconomici"),{
          projectId: richiesta.projectId,
          clienteId: richiesta.clienteId||null,
          organizationId: richiesta.organizationId,
          tipo:"costo", categoriaId:"materiali", categoriaLabel:"Materiali",
          dati:{ descrizione:riga.descrizione, codice:riga.codice||"", quantita:riga.quantita, prezzoUnitario:"", fornitore:"Magazzino interno" },
          importo, origine:"magazzino", richiestaMaterialeId: richiesta.id || null, creadaDa:currentUser.uid, creadaAt:serverTimestamp(),
          modificataDa:currentUser.uid, modificataAt:serverTimestamp(),
        });
      }
      addToast("Costo registrato ✅","success");
      onClose();
    } catch(err) { addToast(messaggioErrore(err), "error"); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:540, maxHeight:"90vh", overflowY:"auto"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
          <h3 style={{fontSize:15,fontWeight:700}}>💰 Registra costo economico</h3>
          <button className="btn-icon" onClick={onClose}><X size={16}/></button>
        </div>
        <div style={{background:"#f9fafb",borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:13}}>
          <span style={{fontWeight:600}}>Commessa:</span> {richiesta.projectName}
        </div>
        <div style={{fontSize:11,color:"#6b7280",marginBottom:10}}>Scrivi il costo solo per i materiali che non hai ancora contabilizzato — precompilato dal Listino Prezzi dove disponibile, sempre modificabile.</div>
        {(richiesta.righe||[]).map((riga,idx)=>{
          const richiesto = numero(riga.quantita), evaso = numero(riga.evaso);
          const suggerito = suggerisciCosto(riga, evaso||richiesto);
          return (
            <div key={idx} style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,paddingBottom:8,borderBottom:"1px solid #f3f4f6"}}>
              <span style={{fontSize:12,flex:1,color:"#374151"}}>{riga.descrizione}{riga.note?` — ${riga.note}`:""} (evasi {evaso} di {richiesto} {riga.unitaMisura})</span>
              {suggerito && <span style={{fontSize:9,color:"#059669",fontWeight:700,flexShrink:0}} title="Suggerito dal Listino Prezzi, sulla quantità evasa">📋</span>}
              <input className="input" type="text" inputMode="decimal" style={{width:120}} placeholder="€ Costo tot."
                value={costiRighe[idx] ?? ""} onChange={e=>setCostiRighe(c=>({...c,[idx]:e.target.value}))}/>
              {suggerito && !costiRighe[idx] && (
                <button type="button" onClick={()=>setCostiRighe(c=>({...c,[idx]:suggerito}))}
                  style={{fontSize:10,color:"#7c3aed",background:"none",border:"none",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>usa {suggerito}</button>
              )}
            </div>
          );
        })}
        <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:10}}>
          <button className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button onClick={submit} disabled={saving} className="btn btn-primary">{saving?"Salvo...":"Registra costo"}</button>
        </div>
      </div>
    </div>
  );
}


// Chiusura — solo se tutte le righe sono a saldo, oppure con una motivazione tracciata per
// ciascuna riga che resta con un saldo residuo. Non si può chiudere lasciando un saldo senza
// spiegazione, e la richiesta non diventa mai "chiusa" da sola: serve sempre questa azione
// esplicita, così resta un momento preciso da tracciare.
function ChiudiRichiestaModal({ richiesta, currentUser, onClose }) {
  const { addToast } = useToast();
  const righeInSospeso = (richiesta.righe||[]).filter(r => numero(r.quantita) - numero(r.evaso) > 0);
  const [motivi, setMotivi] = useState(() => Object.fromEntries(righeInSospeso.map((r,i)=>[i,""])));
  const [saving, setSaving] = useState(false);

  const tutteMotivate = righeInSospeso.every((r,i) => motivi[i]?.trim());

  const conferma = async () => {
    if (righeInSospeso.length>0 && !tutteMotivate) { addToast("Scrivi una motivazione per ogni riga con saldo residuo","error"); return; }
    setSaving(true);
    try {
      const righeAggiornate = (richiesta.righe||[]).map(r => {
        const idxSospeso = righeInSospeso.indexOf(r);
        return idxSospeso===-1 ? r : { ...r, motivoChiusuraConSaldo: motivi[idxSospeso].trim() };
      });
      await updateDoc(doc(db,"richiesteMateriali",richiesta.id), {
        righe: righeAggiornate,
        stato: "chiusa",
        chiusaDa: currentUser.uid, chiusaDaNome: currentUser.displayName||"Utente",
        chiusaData: serverTimestamp(), updatedAt: serverTimestamp(),
      });
      logModifica({
        orgId: richiesta.organizationId, uid: currentUser.uid, nomeUtente: currentUser.displayName||"Utente",
        sezione: "richiesteMateriali", refId: richiesta.id, refNome: `Richiesta materiali — ${richiesta.projectName||""}`,
        campo: "stato", campoLabel: righeInSospeso.length>0 ? "Richiesta chiusa con saldo residuo motivato" : "Richiesta chiusa a saldo",
        before: richiesta.stato, after: "chiusa",
        projectId: richiesta.projectId, projectName: richiesta.projectName||"",
      });
      addToast("Richiesta chiusa ✅","success");
      onClose();
    } catch(err) { addToast(messaggioErrore(err), "error"); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:560}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <h3 style={{fontSize:15,fontWeight:700}}>Chiudi richiesta</h3>
          <button className="btn-icon" onClick={onClose}><X size={16}/></button>
        </div>
        {righeInSospeso.length===0 ? (
          <div style={{fontSize:13,color:"#059669",background:"#d1fae5",padding:"10px 14px",borderRadius:8,marginBottom:16}}>
            ✅ Tutte le righe sono a saldo — nessuna motivazione necessaria.
          </div>
        ) : (
          <>
            <div style={{display:"flex",gap:8,background:"#fffbeb",border:"1px solid #fde68a",borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:12.5,color:"#92400e"}}>
              <AlertTriangle size={15} style={{flexShrink:0,marginTop:1}}/>
              <div>{righeInSospeso.length} material{righeInSospeso.length>1?"i hanno":"e ha"} ancora un saldo residuo. Per chiudere comunque la richiesta, scrivi il motivo per ciascuno (es. irreperibile, obsoleto, ordine errato).</div>
            </div>
            {righeInSospeso.map((r,i) => (
              <div key={i} style={{marginBottom:12,paddingBottom:12,borderBottom:"1px solid #f3f4f6"}}>
                <div style={{fontSize:13,fontWeight:600,marginBottom:2}}>{r.descrizione}</div>
                <div style={{fontSize:11.5,color:"#9ca3af",marginBottom:6}}>Richiesti {r.quantita} {r.unitaMisura} · Evasi {numero(r.evaso)} · Saldo {numero(r.quantita)-numero(r.evaso)}</div>
                <input className="input" placeholder="Motivo del saldo residuo *" value={motivi[i]}
                  onChange={e=>setMotivi(m=>({...m,[i]:e.target.value}))}/>
              </div>
            ))}
          </>
        )}
        <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:8}}>
          <button className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button onClick={conferma} disabled={saving||(righeInSospeso.length>0&&!tutteMotivate)} className="btn btn-primary">
            {saving?"Chiusura...":"Chiudi richiesta"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MagazzinoPage({ onNavigate }) {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId } = useOrg();
  const { addToast } = useToast();
  const [richieste, setRichieste] = useState([]);
  const [orgInfo, setOrgInfo] = useState(null);
  const [filtroStato, setFiltroStato] = useState("attive"); // attive | tutte | evase
  const [search, setSearch] = useState("");
  const [filtroCommessaMag, setFiltroCommessaMag] = useState("tutte");
  const [aperte, setAperte] = useState({});
  const [chiudiTarget, setChiudiTarget] = useState(null);
  const [costoTarget, setCostoTarget] = useState(null);
  const [evasoLocale, setEvasoLocale] = useState({}); // "{richiestaId}::{idx}" -> valore digitato, per non far "saltare" l'input mentre si scrive
  const [mostraRiepilogo, setMostraRiepilogo] = useState(false);
  const timersEvaso = useRef({});
  const canSee = useCanSee();
  const puoAccedere = isOrgAdmin || isPlatformOwner || canSee("magazzino");
  const canGestireMagazzino = isOrgAdmin || isPlatformOwner || canSee("modifica-magazzino");

  useEffect(()=>{
    if (!effectiveOrgId) return;
    getDoc(doc(db,"organizations",effectiveOrgId)).then(s=>{if(s.exists())setOrgInfo(s.data());});
  },[effectiveOrgId]);

  const [progettiInfo, setProgettiInfo] = useState({});
  useEffect(()=>{
    if (!effectiveOrgId) return;
    return onSnapshot(query(collection(db,"projects"),where("organizationId","==",effectiveOrgId)), snap=>{
      const mappa = {};
      snap.docs.forEach(d=>{ const p=d.data(); mappa[d.id] = { codiceCommessa:p.codiceCommessa||"", clienteNome:p.clienteNome||"" }; });
      setProgettiInfo(mappa);
    });
  },[effectiveOrgId]);

  const codiceDi = r => progettiInfo[r.projectId]?.codiceCommessa || r.codiceCommessa || "";
  const nomeConCodice = r => { const c = codiceDi(r); return c ? `[${c}] ${r.projectName||"—"}` : (r.projectName||"—"); };

  useEffect(()=>{
    if (!effectiveOrgId) return;
    const q = query(collection(db,"richiesteMateriali"), where("organizationId","==",effectiveOrgId));
    return onSnapshot(q, snap=>{
      const list = snap.docs.map(d=>({id:d.id,...d.data()})).filter(r=>r.statoRichiesta!=="Da inviare"); // esclude le bozze, mai inviate al magazzino
      list.sort((a,b)=>{
        // Urgenti prima, poi per data
        if (a.urgenza==="urgente"&&b.urgenza!=="urgente") return -1;
        if (b.urgenza==="urgente"&&a.urgenza!=="urgente") return 1;
        const statoOrder = { in_attesa:0, in_lavorazione:1, parziale:2, chiusa:3, annullata:4 };
        if (statoOrder[a.stato]!==statoOrder[b.stato]) return statoOrder[a.stato]-statoOrder[b.stato];
        return (b.createdAt?.toMillis()||0)-(a.createdAt?.toMillis()||0);
      });
      setRichieste(list);
    });
  },[effectiveOrgId]);

  const filtrate = useMemo(()=>{
    let lista = richieste;
    if (filtroStato==="attive") lista = lista.filter(r=>["in_attesa","in_lavorazione","parziale"].includes(r.stato));
    else if (filtroStato==="evase") lista = lista.filter(r=>r.stato==="chiusa"||r.stato==="annullata");
    if (filtroCommessaMag!=="tutte") lista = lista.filter(r=>r.projectName===filtroCommessaMag);
    if (search.trim()) {
      const s = search.toLowerCase();
      lista = lista.filter(r=>
        r.projectName?.toLowerCase().includes(s) ||
        r.richiestaFromName?.toLowerCase().includes(s) ||
        (r.righe||[]).some(riga=>riga.descrizione?.toLowerCase().includes(s)||riga.codice?.toLowerCase().includes(s))
      );
    }
    return lista;
  },[richieste, filtroStato, search, filtroCommessaMag]);

  const commesseMag = useMemo(()=>[...new Set(richieste.map(r=>r.projectName).filter(Boolean))].sort(),[richieste]);

  // Riepilogo dinamico — somma per singolo materiale (stessa descrizione + stessa unità di
  // misura) su tutte le richieste ancora attive, per dare al magazziniere un colpo d'occhio
  // su cosa evadere senza dover aprire ogni richiesta una per una. Sempre ricalcolato dal
  // vivo, mai un dato salvato a parte che potrebbe disallinearsi.
  const riepilogoMateriali = useMemo(() => {
    const richiesteAttive = richieste.filter(r=>["in_attesa","in_lavorazione","parziale"].includes(r.stato));

    // Quante commesse aperte diverse ha ciascun cliente, tra quelle con una richiesta ancora
    // attiva — se una sola, il nome del cliente da solo basta a riconoscerla senza ambiguità
    // (utile per scrivere sullo scatolo pronto per il cantiere); se più di una, serve anche
    // il codice commessa per non confondere quale scatolo va a quale cantiere.
    const progettiPerCliente = {};
    richiesteAttive.forEach(r => {
      const cliente = progettiInfo[r.projectId]?.clienteNome || "";
      if (!cliente) return;
      if (!progettiPerCliente[cliente]) progettiPerCliente[cliente] = new Set();
      progettiPerCliente[cliente].add(r.projectId);
    });
    const etichettaCommessa = r => {
      const cliente = progettiInfo[r.projectId]?.clienteNome || "";
      if (!cliente) return nomeConCodice(r); // nessun cliente collegato, resta come prima
      const ambigua = (progettiPerCliente[cliente]?.size||0) > 1;
      return ambigua ? `${cliente} ${codiceDi(r)} ${r.projectName||""}`.trim() : cliente;
    };

    const mappa = {};
    richiesteAttive.forEach(r => {
      (r.righe||[]).forEach(riga => {
        if (!riga.descrizione?.trim()) return;
        const chiave = `${norm(riga.descrizione)}|${norm(riga.unitaMisura)}`;
        if (!mappa[chiave]) mappa[chiave] = { descrizione:riga.descrizione, codice:riga.codice||"", unitaMisura:riga.unitaMisura, richiesto:0, evaso:0, commesse:new Set() };
        mappa[chiave].richiesto += numero(riga.quantita);
        mappa[chiave].evaso += numero(riga.evaso);
        mappa[chiave].commesse.add(etichettaCommessa(r));
      });
    });
    return Object.values(mappa).map(m => ({...m, saldo: m.richiesto-m.evaso, commesse:[...m.commesse]}))
      .filter(m => m.saldo > 0)
      .sort((a,b) => b.saldo-a.saldo);
  }, [richieste, progettiInfo]);

  const toggleAperta = id => setAperte(a=>({...a,[id]:!a[id]}));

  const segnaInLavorazione = async id => {
    await updateDoc(doc(db,"richiesteMateriali",id),{stato:"in_lavorazione",updatedAt:serverTimestamp()});
  };

  // Evaso — inline, senza nessuna finestra o conferma: si scrive il numero e basta, salva da
  // solo dopo una breve pausa di battitura. Non può mai superare la quantità richiesta.
  const aggiornaEvasoInline = (richiesta, idx, valoreDigitato) => {
    const chiave = `${richiesta.id}::${idx}`;
    setEvasoLocale(s => ({...s, [chiave]: valoreDigitato}));
    if (timersEvaso.current[chiave]) clearTimeout(timersEvaso.current[chiave]);
    timersEvaso.current[chiave] = setTimeout(async () => {
      const riga = richiesta.righe[idx];
      const richiesto = numero(riga.quantita);
      let nuovoEvaso = numero(valoreDigitato);
      if (nuovoEvaso > richiesto) {
        addToast(`Non puoi evadere più di quanto richiesto (${richiesto} ${riga.unitaMisura})`,"error");
        nuovoEvaso = richiesto;
        setEvasoLocale(s => ({...s, [chiave]: String(nuovoEvaso)}));
      }
      const righeAggiornate = richiesta.righe.map((r,i) => i===idx ? {...r, evaso: String(nuovoEvaso)} : r);
      const tutteASaldo = righeAggiornate.every(r => numero(r.quantita)-numero(r.evaso)<=0);
      const qualcheProgresso = righeAggiornate.some(r => numero(r.evaso)>0);
      await updateDoc(doc(db,"richiesteMateriali",richiesta.id), {
        righe: righeAggiornate,
        stato: tutteASaldo ? richiesta.stato : (qualcheProgresso ? "parziale" : richiesta.stato),
        evasoDa: currentUser.uid, evasoAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });
      delete timersEvaso.current[chiave];
    }, 600);
  };

  const nAttive = richieste.filter(r=>["in_attesa","in_lavorazione","parziale"].includes(r.stato)).length;
  const nUrgenti = richieste.filter(r=>r.urgenza==="urgente"&&["in_attesa","in_lavorazione","parziale"].includes(r.stato)).length;

  // Export Excel — una riga per ogni materiale, di tutte le richieste attualmente filtrate
  const esportaExcel = () => {
    const intestazione = ["Richiesta","Commessa","Data","Richiesto da","Urgenza","Stato","Descrizione materiale","Codice","Quantità","Evaso","A Saldo","U.M.","Note riga"];
    const righeExcel = [];
    filtrate.forEach(r=>{
      const dataR = r.createdAt?.toDate?.()?.toLocaleDateString("it-IT")||"—";
      (r.righe||[]).forEach(riga=>{
        const richiesto=numero(riga.quantita), evaso=numero(riga.evaso);
        righeExcel.push([
          r.id.slice(0,6).toUpperCase(), nomeConCodice(r), dataR, r.richiestaFromName||"—",
          r.urgenza==="urgente"?"Urgente":"Normale", STATO_CONFIG[r.stato]?.label||r.stato,
          riga.descrizione||"—", riga.codice||"—", riga.quantita||"—", evaso, richiesto-evaso, riga.unitaMisura||"—", riga.note||""
        ]);
      });
    });
    if (righeExcel.length===0) { addToast("Nessun materiale da esportare con i filtri attuali","warning"); return; }
    esportaXLSX([intestazione,...righeExcel], `Richieste_Magazzino_${new Date().toLocaleDateString("it-IT").replace(/\//g,"-")}`, "Magazzino");
  };

  // Stampa PDF — lista di preparazione materiali per il magazzino, sulle richieste attualmente filtrate
  // Stampa PDF del riepilogo — una lista semplice, pensata per essere inoltrata così com'è
  // a un fornitore: solo cosa manca, senza dettagli interni di richiesto/evaso.
  const stampaPDFRiepilogo = () => {
    if (riepilogoMateriali.length===0) { addToast("Nessun materiale da stampare","warning"); return; }
    const win = window.open("","_blank");
    const logoHtml = orgInfo?.logo?`<img src="${orgInfo.logo}" style="height:36px;object-fit:contain;"/>`:
      `<div style="font-size:16px;font-weight:900;color:#1e1b4b;">◆ ${orgInfo?.nome||orgInfo?.name||""}</div>`;
    const righeHtml = riepilogoMateriali.map(m=>`
      <tr>
        <td style="border:1px solid #e5e7eb;padding:6px 10px;">${escapeHtml(m.descrizione)}</td>
        <td style="border:1px solid #e5e7eb;padding:6px 10px;">${escapeHtml(m.codice)||"—"}</td>
        <td style="border:1px solid #e5e7eb;padding:6px 10px;text-align:center;font-weight:700;">${m.saldo}</td>
        <td style="border:1px solid #e5e7eb;padding:6px 10px;">${escapeHtml(m.unitaMisura)}</td>
        <td style="border:1px solid #e5e7eb;padding:6px 10px;font-size:10px;color:#6b7280;">${m.commesse.map(escapeHtml).join(", ")}</td>
      </tr>`).join("");
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Riepilogo Materiali da Ordinare</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;color:#111;padding:14mm;font-size:12px;}
      @media screen{body{max-width:800px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4;margin:14mm;}
      h1{font-size:18px;color:#1e1b4b;margin-bottom:2px;}
      .sub{font-size:11px;color:#6b7280;margin-bottom:16px;}
      table{width:100%;border-collapse:collapse;}
      th{border:1px solid #e5e7eb;padding:6px 10px;text-align:left;background:#f9fafb;font-size:11px;color:#374151;}
    </style></head><body>
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;">${logoHtml}<div class="sub" style="margin:0;">Generato il ${new Date().toLocaleDateString("it-IT")}</div></div>
      <h1>Riepilogo Materiali da Ordinare</h1>
      <div class="sub">Aggiornato al ${new Date().toLocaleDateString("it-IT",{day:"2-digit",month:"2-digit",year:"numeric"})}</div>
      <table>
        <thead><tr><th>Materiale</th><th>Codice</th><th style="text-align:center;">Da ordinare</th><th>U.M.</th><th>Da quali commesse</th></tr></thead>
        <tbody>${righeHtml}</tbody>
      </table>
    </body></html>`);
    win.document.close();
    setTimeout(() => {
      if (win.closed) return;
      const style = win.document.createElement("style");
      style.textContent = "@media print { .no-print-btn { display:none !important; } }";
      win.document.head.appendChild(style);
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print-btn";
      btn.onclick = () => win.print();
      btn.style.cssText = "position:fixed;top:16px;right:16px;z-index:9999;padding:10px 20px;background:#1e1b4b;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,.2);";
      win.document.body.appendChild(btn);
    }, 50);
  };

  const stampaPDF = () => {
    if (filtrate.length===0) { addToast("Nessuna richiesta da stampare con i filtri attuali","warning"); return; }
    const win = window.open("","_blank");
    const logoHtml = orgInfo?.logo?`<img src="${orgInfo.logo}" style="height:36px;object-fit:contain;"/>`:
      `<div style="font-size:16px;font-weight:900;color:#1e1b4b;">◆ ${orgInfo?.nome||orgInfo?.name||""}</div>`;

    const blocchi = filtrate.map(r=>{
      const dataR = r.createdAt?.toDate?.()?.toLocaleDateString("it-IT")||"—";
      const righeHtml = (r.righe||[]).map(riga=>{
        const richiesto=numero(riga.quantita), evaso=numero(riga.evaso), saldo=richiesto-evaso;
        return `
        <tr>
          <td style="border:1px solid #e5e7eb;padding:5px 8px;">☐</td>
          <td style="border:1px solid #e5e7eb;padding:5px 8px;">${riga.descrizione||"—"}</td>
          <td style="border:1px solid #e5e7eb;padding:5px 8px;">${riga.codice||"—"}</td>
          <td style="border:1px solid #e5e7eb;padding:5px 8px;text-align:center;font-weight:700;">${riga.quantita||"—"} ${riga.unitaMisura||""}</td>
          <td style="border:1px solid #e5e7eb;padding:5px 8px;text-align:center;">${evaso}</td>
          <td style="border:1px solid #e5e7eb;padding:5px 8px;text-align:center;font-weight:700;color:${saldo>0?"#d97706":"#059669"};">${saldo}</td>
          <td style="border:1px solid #e5e7eb;padding:5px 8px;font-size:10px;color:#6b7280;">${riga.note||""}</td>
        </tr>`;}).join("");
      return `
        <div style="page-break-inside:avoid;margin-bottom:18px;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
          <div style="background:#1e1b4b;color:#fff;padding:8px 12px;display:flex;justify-content:space-between;align-items:center;">
            <span style="font-weight:700;">📁 ${nomeConCodice(r)}</span>
            <span>${r.urgenza==="urgente"?`<span style="background:#dc2626;padding:2px 8px;border-radius:10px;font-size:10px;font-weight:700;margin-right:8px;">URGENTE</span>`:""}${STATO_CONFIG[r.stato]?.label||r.stato}</span>
          </div>
          <div style="padding:8px 12px;font-size:11px;color:#6b7280;border-bottom:1px solid #e5e7eb;">
            Richiesto da ${r.richiestaFromName||"—"} il ${dataR}
            ${r.motivoUrgenza?` · Motivo urgenza: ${r.motivoUrgenza}`:""}
            ${r.noteGenerali?` · Note: ${r.noteGenerali}`:""}
          </div>
          <table style="width:100%;border-collapse:collapse;font-size:11px;">
            <thead><tr style="background:#f9fafb;">
              <th style="border:1px solid #e5e7eb;padding:5px 8px;width:26px;">✓</th>
              <th style="border:1px solid #e5e7eb;padding:5px 8px;text-align:left;">Descrizione materiale</th>
              <th style="border:1px solid #e5e7eb;padding:5px 8px;text-align:left;width:100px;">Codice</th>
              <th style="border:1px solid #e5e7eb;padding:5px 8px;width:80px;">Quantità</th>
              <th style="border:1px solid #e5e7eb;padding:5px 8px;width:60px;">Evaso</th>
              <th style="border:1px solid #e5e7eb;padding:5px 8px;width:60px;">A Saldo</th>
              <th style="border:1px solid #e5e7eb;padding:5px 8px;text-align:left;">Note</th>
            </tr></thead>
            <tbody>${righeHtml}</tbody>
          </table>
        </div>`;
    }).join("");

    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Preparazione Materiali Magazzino</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;color:#111;padding:12mm 16mm;}
      @media screen{body{max-width:800px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4;}
      .hdr{border-bottom:3px solid #1e1b4b;padding-bottom:8px;margin-bottom:14px;display:flex;justify-content:space-between;align-items:center;}
      .footer{border-top:1px solid #e5e7eb;margin-top:14px;padding-top:6px;font-size:9px;color:#9ca3af;display:flex;justify-content:space-between;}
    </style></head><body>
    <div class="hdr">
      <div>${logoHtml}</div>
      <div style="text-align:right">
        <div style="font-size:15px;font-weight:800;color:#1e1b4b">PREPARAZIONE MATERIALI</div>
        <div style="font-size:11px;color:#6b7280">${filtrate.length} richiest${filtrate.length===1?"a":"e"} · Stampato il ${new Date().toLocaleDateString("it-IT")}</div>
      </div>
    </div>
    ${blocchi}
    <div class="footer">
      <span>Generato da Nexiora — ${dominioSito()}</span>
      <span>Ad uso interno magazzino</span>
    </div>
    
    </body></html>`);
    win.document.close();
    setTimeout(()=>{
      if (win.closed) return;
      const style = win.document.createElement("style");
      style.textContent = "@media print { .no-print-btn { display:none !important; } }";
      win.document.head.appendChild(style);
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print-btn";
      btn.onclick = () => win.print();
      btn.style.cssText = "position:fixed;top:16px;right:16px;z-index:9999;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,.2);";
      win.document.body.appendChild(btn);
    }, 50);
  };

  if (!puoAccedere) {
    return (
      <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100%",padding:40,textAlign:"center"}}>
        <div style={{fontSize:40,marginBottom:12}}>🔒</div>
        <div style={{fontSize:15,fontWeight:700,color:"#374151",marginBottom:6}}>Accesso riservato</div>
        <div style={{fontSize:13,color:"#9ca3af",maxWidth:360,marginBottom:16}}>Questa sezione è visibile solo al Magazziniere e agli utenti autorizzati dall'Amministratore. Per creare una richiesta di materiale, usa il pulsante qui sotto.</div>
        {onNavigate && (
          <button onClick={()=>onNavigate({page:"ordini-materiali"})}
            style={{padding:"9px 20px",background:"#7c3aed",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            Vai a Ordini materiali al Magazzino →
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      {/* Header */}
      <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"14px 24px",flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div>
            <h2 style={{fontSize:18,fontWeight:700,display:"flex",alignItems:"center",gap:8}}>
              <Package size={18} color="#7c3aed"/> Evasione richieste al magazzino
              {!mostraRiepilogo && (
                <>
                  <button onClick={esportaExcel}
                    style={{padding:"4px 12px",border:"1px solid #059669",borderRadius:8,background:"#f0fdf4",color:"#059669",
                      fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif",
                      display:"flex",alignItems:"center",gap:5,whiteSpace:"nowrap"}}>
                    📊 Excel
                  </button>
                  <button onClick={stampaPDF}
                    style={{padding:"4px 12px",border:"1px solid #2563eb",borderRadius:8,background:"#eff6ff",color:"#1d4ed8",
                      fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif",
                      display:"flex",alignItems:"center",gap:5,whiteSpace:"nowrap"}}>
                    🖨️ PDF
                  </button>
                </>
              )}
              {mostraRiepilogo && (
                <button onClick={stampaPDFRiepilogo}
                  style={{padding:"4px 12px",border:"1px solid #2563eb",borderRadius:8,background:"#eff6ff",color:"#1d4ed8",
                    fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif",
                    display:"flex",alignItems:"center",gap:5,whiteSpace:"nowrap"}}>
                  🖨️ Stampa PDF riepilogo
                </button>
              )}
              <button onClick={()=>setMostraRiepilogo(v=>!v)}
                style={{padding:"4px 12px",border:`1px solid ${mostraRiepilogo?"#7c3aed":"#d1d5db"}`,borderRadius:8,background:mostraRiepilogo?"#7c3aed":"#fff",color:mostraRiepilogo?"#fff":"#374151",
                  fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Inter',sans-serif",
                  display:"flex",alignItems:"center",gap:5,whiteSpace:"nowrap"}}>
                {mostraRiepilogo ? "⬅ Evasione richieste magazzino" : `📊 Riepilogo materiali${riepilogoMateriali.length>0?` (${riepilogoMateriali.length})`:""}`}
              </button>
              <InsightButton onNavigate={onNavigate}/>
            </h2>
            {!mostraRiepilogo && (
            <div style={{display:"flex",gap:8,marginTop:8,flexWrap:"wrap"}}>
            {[{val:"attive",label:"Attive"},{val:"tutte",label:"Tutte"},{val:"evase",label:"Storico"}].map(f=>(
              <button key={f.val} onClick={()=>setFiltroStato(f.val)}
                style={{padding:"6px 14px",borderRadius:8,border:`1px solid ${filtroStato===f.val?"#7c3aed":"#e5e7eb"}`,
                  background:filtroStato===f.val?"#7c3aed":"#fff",color:filtroStato===f.val?"#fff":"#6b7280",
                  fontSize:13,fontWeight:filtroStato===f.val?600:400,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                {f.label}
              </button>
            ))}
            <select value={filtroCommessaMag} onChange={e=>setFiltroCommessaMag(e.target.value)}
              style={{border:"1px solid #e5e7eb",borderRadius:8,padding:"6px 12px",fontSize:13,fontFamily:"'Inter',sans-serif",outline:"none"}}>
              <option value="tutte">Tutte le commesse</option>
              {commesseMag.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
            {filtroCommessaMag!=="tutte" && (
              <button onClick={()=>setFiltroCommessaMag("tutte")}
                style={{display:"flex",alignItems:"center",gap:4,padding:"6px 10px",border:"1px solid #e5e7eb",borderRadius:8,background:"#fff",cursor:"pointer",fontSize:12,fontFamily:"'Inter',sans-serif"}}>
                <X size={13}/> Resetta
              </button>
            )}
            </div>
            )}
          </div>
        </div>
      </div>

      {mostraRiepilogo ? (
        /* Riepilogo materiali — vista a sé, pensata per essere stampata e inoltrata al
           fornitore: solo cosa manca, senza il dettaglio di richiesto/evaso che non gli serve. */
        <div style={{flex:1,overflowY:"auto",padding:24}}>
          <div style={{background:"#fff",border:"1px solid #e9d5ff",borderRadius:12,padding:16}}>
            <div style={{fontSize:14,fontWeight:700,color:"#1e1b4b",marginBottom:2}}>📊 Riepilogo materiali per saldo</div>
            <div style={{fontSize:11.5,color:"#9ca3af",marginBottom:12}}>Aggiornato al {new Date().toLocaleDateString("it-IT",{day:"2-digit",month:"2-digit",year:"numeric"})}</div>
            {riepilogoMateriali.length===0 ? (
              <div style={{textAlign:"center",padding:20,color:"#9ca3af",fontSize:12.5}}>Nessun materiale con un saldo residuo al momento.</div>
            ) : (
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                <thead>
                  <tr style={{background:"#faf5ff"}}>
                    {["Materiale","Codice","Da ordinare","U.M.","Da quali commesse"].map(h=>(
                      <th key={h} style={{padding:"6px 10px",textAlign:"left",fontSize:11,fontWeight:600,color:"#6d28d9"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {riepilogoMateriali.map((m,i)=>(
                    <tr key={i} style={{borderBottom:"1px solid #f3f4f6"}}>
                      <td style={{padding:"7px 10px",fontWeight:600}}>{m.descrizione}</td>
                      <td style={{padding:"7px 10px",color:"#7c3aed",fontFamily:"monospace"}}>{m.codice||"—"}</td>
                      <td style={{padding:"7px 10px",fontWeight:700,color:"#d97706"}}>{m.saldo}</td>
                      <td style={{padding:"7px 10px",color:"#6b7280"}}>{m.unitaMisura}</td>
                      <td style={{padding:"7px 10px",fontSize:11,color:"#9ca3af"}}>{m.commesse.join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : (
      /* Lista richieste */
      <div style={{flex:1,overflowY:"auto",padding:24}}>
        <PrimoAccessoSezione sezioneId="magazzino"/>
        {filtrate.length===0 ? (
          <div style={{textAlign:"center",padding:64,color:"#9ca3af"}}>
            <Package size={48} style={{marginBottom:12,opacity:0.2}}/>
            <div style={{fontSize:15,fontWeight:600}}>
              {filtroStato==="attive" ? "Nessuna richiesta in coda" : "Nessuna richiesta trovata"}
            </div>
            <div style={{fontSize:13,marginTop:4}}>Le richieste create dai PM e dagli operai appariranno qui</div>
          </div>
        ) : filtrate.map(r=>{
          const cfg = STATO_CONFIG[r.stato]||STATO_CONFIG.in_attesa;
          const isAperta = aperte[r.id];
          const isAttiva = ["in_attesa","in_lavorazione","parziale"].includes(r.stato);
          return (
            <div key={r.id} style={{background:"#fff",border:`1px solid ${r.urgenza==="urgente"&&isAttiva?"#fca5a5":"#e5e7eb"}`,
              borderRadius:12,marginBottom:12,overflow:"hidden",borderLeft:`4px solid ${cfg.colore}`}}>
              {/* Header */}
              <div style={{display:"flex",alignItems:"center",gap:10,padding:"12px 16px",cursor:"pointer"}} onClick={()=>toggleAperta(r.id)}>
                <span style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:12,background:cfg.bg,color:cfg.colore,flexShrink:0}}>{cfg.icon} {cfg.label}</span>
                {r.urgenza==="urgente"&&isAttiva && <span style={{fontSize:11,fontWeight:700,color:"#dc2626",background:"#fee2e2",padding:"2px 8px",borderRadius:12,flexShrink:0}}>🚨 URGENTE</span>}
                <div style={{flex:1,minWidth:0}}>
                  <CommessaLink commessa={{id:r.projectId,name:r.projectName}} style={{fontSize:13,fontWeight:700,display:"block",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textAlign:"left"}}>{nomeConCodice(r)}</CommessaLink>
                  <div style={{fontSize:11,color:"#9ca3af"}}>{r.righe?.length} materiali · da {r.richiestaFromName} · {r.createdAt?.toDate?.()?.toLocaleDateString("it-IT")}</div>
                </div>
                {isAperta ? <ChevronUp size={15} color="#9ca3af"/> : <ChevronDown size={15} color="#9ca3af"/>}
              </div>

              {/* Dettaglio */}
              {isAperta && (
                <div style={{borderTop:"1px solid #f3f4f6",padding:"12px 16px"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,marginBottom:8,tableLayout:"fixed"}}>
                    <colgroup>
                      <col style={{width:"auto"}}/>
                      <col style={{width:70}}/>
                      <col style={{width:64}}/>
                      <col style={{width:70}}/>
                      <col style={{width:70}}/>
                      <col style={{width:56}}/>
                      <col style={{width:"auto"}}/>
                    </colgroup>
                    <thead>
                      <tr style={{background:"#f9fafb"}}>
                        {["Descrizione","Codice","Qta","Evaso","A Saldo","U.M.","Note"].map(h=>(
                          <th key={h} style={{padding:"6px 10px",textAlign:["Qta","Evaso","A Saldo"].includes(h)?"right":"left",fontSize:11,fontWeight:600,color:"#6b7280"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(r.righe||[]).map((riga,i)=>{
                        const chiave = `${r.id}::${i}`;
                        const evasoMostrato = evasoLocale[chiave] !== undefined ? evasoLocale[chiave] : (riga.evaso ?? "0");
                        const richiesto=numero(riga.quantita), evaso=numero(evasoMostrato), saldo=richiesto-evaso;
                        const editabile = isAttiva && canGestireMagazzino;
                        return (
                        <tr key={i} style={{borderBottom:"1px solid #f3f4f6",background:i%2===1?"#fbfbfd":"transparent"}}>
                          <td style={{padding:"7px 10px",fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{riga.descrizione}</td>
                          <td onClick={()=>onNavigate&&onNavigate({page:"listino-materiali"})}
                          style={{padding:"7px 10px",color:"#7c3aed",fontFamily:"monospace",cursor:"pointer",textDecoration:"underline",textDecorationColor:"transparent",transition:"text-decoration-color 0.15s"}}
                          onMouseEnter={e=>e.currentTarget.style.textDecorationColor="#7c3aed"}
                          onMouseLeave={e=>e.currentTarget.style.textDecorationColor="transparent"}>
                          {riga.codice||"—"}
                        </td>
                          <td style={{padding:"7px 10px",fontWeight:700,textAlign:"right"}}>{riga.quantita}</td>
                          <td style={{padding:"7px 10px",textAlign:"right"}}>
                            {editabile ? (
                              <input type="text" inputMode="decimal" value={evasoMostrato}
                                onChange={e=>aggiornaEvasoInline(r, i, e.target.value)}
                                style={{width:52,padding:"3px 6px",border:"1px solid #e5e7eb",borderRadius:5,fontSize:12,fontWeight:700,fontFamily:"'Inter',sans-serif",textAlign:"right"}}/>
                            ) : (evaso||0)}
                          </td>
                          <td style={{padding:"7px 10px",textAlign:"right"}}>
                            <span style={{fontWeight:700,padding:saldo>0?"2px 7px":0,borderRadius:5,color:saldo>0?"#dc2626":"#059669",background:saldo>0?"#fef2f2":"transparent"}}>{saldo}</span>
                          </td>
                          <td style={{padding:"7px 10px",color:"#6b7280"}}>{riga.unitaMisura}</td>
                          <td style={{padding:"7px 10px",color:"#9ca3af",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{riga.note||"—"}</td>
                        </tr>
                      );})}
                    </tbody>
                  </table>
                  {(() => {
                    const saldoTotale = (r.righe||[]).reduce((s,rg)=>s+Math.max(0,numero(rg.quantita)-numero(rg.evaso)),0);
                    const qualcheProgresso = (r.righe||[]).some(rg=>numero(rg.evaso)>0);
                    return isAttiva && qualcheProgresso && saldoTotale>0 && (
                      <div style={{display:"flex",gap:6,alignItems:"center",fontSize:12,fontWeight:600,color:"#dc2626",background:"#fef2f2",border:"1px solid #fca5a5",padding:"6px 10px",borderRadius:6,marginBottom:8}}>
                        <AlertTriangle size={13} style={{flexShrink:0}}/>
                        <div>Ordine non evaso completamente — {saldoTotale} unità ancora a saldo.</div>
                      </div>
                    );
                  })()}
                  {r.noteGenerali && <div style={{fontSize:12,color:"#6b7280",background:"#f9fafb",padding:"6px 10px",borderRadius:6,marginBottom:6}}>📝 Note: {r.noteGenerali}</div>}
                  {r.noteEvasione && <div style={{fontSize:12,color:"#059669",background:"#d1fae5",padding:"6px 10px",borderRadius:6,marginBottom:6}}>✅ Evasione: {r.noteEvasione}</div>}
                  {(r.righe||[]).filter(rg=>rg.motivoChiusuraConSaldo).map((rg,i)=>(
                    <div key={i} style={{fontSize:12,color:"#92400e",background:"#fffbeb",padding:"6px 10px",borderRadius:6,marginBottom:6}}>
                      ⚠️ Chiusa con saldo su "{rg.descrizione}": {rg.motivoChiusuraConSaldo}
                    </div>
                  ))}
                  {r.stato==="chiusa" && r.chiusaDaNome && (
                    <div style={{fontSize:11,color:"#9ca3af",marginBottom:6}}>Chiusa da {r.chiusaDaNome} il {r.chiusaData?.toDate?.()?.toLocaleDateString("it-IT")}</div>
                  )}

                  {/* Azioni magazzino — solo per chi ha il permesso di gestione */}
                  {isAttiva && canGestireMagazzino && (
                    <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:4}}>
                      {r.stato==="in_attesa" && (
                        <button onClick={()=>segnaInLavorazione(r.id)}
                          style={{padding:"7px 14px",border:"1px solid #bfdbfe",borderRadius:8,background:"#eff6ff",color:"#2563eb",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                          🔄 Prendo in carico
                        </button>
                      )}
                      <button onClick={()=>setCostoTarget(r)}
                        style={{padding:"7px 14px",border:"1px solid #bbf7d0",borderRadius:8,background:"#f0fdf4",color:"#065f46",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                        💰 Registra costo economico
                      </button>
                      <button onClick={()=>setChiudiTarget(r)}
                        style={{display:"flex",alignItems:"center",gap:5,padding:"7px 14px",border:"1px solid #d1d5db",borderRadius:8,background:"#fff",color:"#374151",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                        <Lock size={12}/> Chiudi richiesta
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}

      {costoTarget && (
        <CostoModal richiesta={costoTarget} onClose={()=>setCostoTarget(null)}/>
      )}
      {chiudiTarget && (
        <ChiudiRichiestaModal richiesta={chiudiTarget} currentUser={currentUser} onClose={()=>setChiudiTarget(null)}/>
      )}
    </div>
  );
}
